import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, createSlots as _createSlots, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "contacts-list" }
const _hoisted_2 = { class: "filter-container" }
const _hoisted_3 = { class: "no-contacts" }

import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { contactsApi } from '@/features/contacts/contacts';
import PersonSearchIcon from '@/shared/components/PersonSearchIcon.vue';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import Toast from '@/shared/services/toast';
import ContactTypePicker from '@/features/contacts/components/ContactTypePicker.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import DataTable from '@/shared/components/dataTable/DataTable.vue';
import ContactDisplayModel from '@/features/contacts/models/contactDisplayModel';
import { contactColumns } from '@/features/contacts/models/contactDataTableColumns';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import PageData from '@/shared/components/dataTable/pageData';
import FriendlyDateTime from '@/shared/components/FriendlyDateTime.vue';
import FriendlyString from '@/shared/components/FriendlyString.vue';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import AttributeIconUtil from '../../../shared/util/attributeIconUtil';
import ColumnPicker from '@/shared/components/dataTable/ColumnPicker.vue';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useContactFilters } from '@/features/contacts/composables/useContactFilters';


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactsList',
  emits: ["loaded"],
  setup(__props, { emit: __emit }) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const contactAttributes = computed<ContactAttributeViewModel[]>(() => store.getters.contactAttributes);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const visibleColumns = ref<DataTableColumn[]>(contactColumns);

const contacts = ref<ContactViewModel[]>([]);
const formattedContacts = ref<ContactDisplayModel[]>([]);
const pageData = ref<PageData>();
const total = ref(0);
const rowsPerPage = ref(25);
const contactType = ref<ContactType>(ContactType.Lead);
const hasInvalidConditions = ref(false);
const previousFilterString = ref('');

const reloading = ref(false);
const filtersManager = useContactFilters();
const filters = ref<ConditionGroupViewModel>({
  type: 'and',
  conditions: [],
});

const router = useRouter();

const emit = __emit;

onBeforeMount(async () => {
  if (!currentWorkspace.value) {
    handleSeriousError('No workspace selected');
    return;
  }
  await loadContacts();
  emit('loaded');
});

async function loadContacts() {
  try {
    reloading.value = true;
    const data = await contactsApi.getContacts(
      {
        offset: 0,
        limit: rowsPerPage.value,
        sort: {
          attribute: 'lastSeen',
          direction: 'desc',
        },
        filters: createFiltersForRequest(),
      },
      currentWorkspace.value.id,
    );
    total.value = data.total;
    contacts.value = data.page;
    formatData();
  } catch (err: any) {
    handleSeriousError(err.message);
    Toast.showError(`Error loading contacts ${err.message}`);
  } finally {
    reloading.value = false;
  }
}

function handleCellClick(contact: ContactViewModel) {
  router.push({
    name: 'contact-view',
    params: { id: contact.id },
  });
}

function getContactAttributesToFormat() {
  // all but name, as we want the default DataTable formatting so that the cell is clickable
  // also making a clone as changing the id of the reference means we'd have to strip the .customAttributes in a couple places
  const contactAttributesToFormat = JSON.parse(JSON.stringify(contactAttributes.value.slice(1)));
  for (const contactAttribute of contactAttributesToFormat) {
    if (!contactAttribute.system) {
      contactAttribute.id = getCustomAttributeId(contactAttribute.id);
    }
  }
  return contactAttributesToFormat;
}

function isNameEmpty(name: string) {
  return name.length === 0 || !name.trim();
}

function getColumns() {
  const allContactColumns: DataTableColumn[] = [];
  allContactColumns.push(...contactColumns);
  allContactColumns.push(...getCustomAttributeColumns());
  return allContactColumns;
}

function getContactName(name: string | undefined): string {
  if (name && !isNameEmpty(name)) {
    return name;
  }
  return 'Unknown';
}

async function onFiltersChanged(update: ConditionGroupUpdate) {
  filters.value = update.conditionGroup;
  hasInvalidConditions.value = update.invalid;

  const newFilterString = JSON.stringify(filters.value);
  if (previousFilterString.value !== newFilterString) {
    await reloadContacts(true);
    previousFilterString.value = newFilterString;
  }
}

async function contactTypeUpdated(newContactType: ContactType) {
  contactType.value = newContactType;
  await reloadContacts(true);
}

async function dataTableChanged(newPageData: PageData) {
  pageData.value = newPageData;
  await reloadContacts(false);
}

function getCustomAttributeColumns(): DataTableColumn[] {
  if (contactAttributes.value) {
    const customAttributes = contactAttributes.value.filter((a: ContactAttributeViewModel) => !a.system);
    return customAttributes.map((attribute: ContactAttributeViewModel) => {
      return {
        display: attribute.name,
        name: getCustomAttributeId(attribute.id),
        sortable: true,
        clickable: false,
        icon: AttributeIconUtil.getIconType(attribute.type, attribute.id, false),
      } as DataTableColumn;
    });
  }
  return [];
}

async function reloadContacts(clearOffset: boolean) {
  // don't automatically reload contacts list if there invalid conditions
  if (hasInvalidConditions.value) {
    return;
  }
  try {
    reloading.value = true;
    const data = await contactsApi.getContacts(
      {
        offset: !clearOffset && pageData.value ? pageData.value.offset : 0,
        limit: rowsPerPage.value,
        sort: {
          attribute: pageData.value ? pageData.value.sortBy : 'lastSeen',
          direction: pageData.value ? pageData.value.sortDirection : 'desc',
        },
        filters: createFiltersForRequest(),
      },
      currentWorkspace.value.id,
    );
    total.value = data.total;
    contacts.value = data.page;
    formatData();
  } catch (err: any) {
    handleSeriousError(err.message);
    Toast.showError(`Error loading contacts ${err.message}`);
  } finally {
    reloading.value = false;
  }
}

function createFiltersForRequest() {
  const typeFilter: ConditionViewModel = {
    attribute: 'type',
    operation: 'equals',
    type: 'string',
    value: contactType.value,
    result: true,
  };

  const req: ConditionGroupViewModel = {
    type: 'and',
    conditions: [typeFilter],
  };

  if (filters.value.conditions.length > 0) {
    req.conditions.push(filters.value);
  }

  return req;
}

function formatData() {
  const newFormattedContacts = [];
  for (const contact of contacts.value) {
    const formattedContact = {
      id: contact.id,
      name: getContactName(contact.name),
      email: contact.email,
      phoneNumber: contact.phoneNumber,
      lastSeen: contact.lastSeenUtc,
      lastContacted: contact.lastContactedUtc,
      lastHeardFrom: contact.lastHeardFromUtc,
      firstSeen: contact.firstSeenUtc,
    };
    addCustomAttributeValues(formattedContact, contact);

    newFormattedContacts.push(formattedContact);
  }
  formattedContacts.value = newFormattedContacts;
}

function addCustomAttributeValues(formattedContact: any, contact: ContactViewModel) {
  const customAttributes = contactAttributes.value.filter((a: ContactAttributeViewModel) => !a.system);
  for (const attribute of customAttributes) {
    formattedContact[getCustomAttributeId(attribute.id)] = contact.customAttributes[attribute.id];
  }
}

function getCustomAttributeId(id: string) {
  return `customAttributes.${id}`;
}

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(ConditionGroupBuilder, {
        value: filters.value,
        manager: _unref(filtersManager),
        disabled: reloading.value,
        onUpdated: onFiltersChanged
      }, {
        before: _withCtx(() => [
          _createVNode(ContactTypePicker, {
            class: "contact-type-picker",
            value: contactType.value,
            disabled: reloading.value,
            onUpdatedType: contactTypeUpdated
          }, null, 8, ["value", "disabled"])
        ]),
        _: 1
      }, 8, ["value", "manager", "disabled"]),
      _createVNode(ColumnPicker, {
        columns: getColumns(),
        modelValue: visibleColumns.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((visibleColumns).value = $event))
      }, null, 8, ["columns", "modelValue"])
    ]),
    _createVNode(DataTable, {
      data: formattedContacts.value,
      columns: visibleColumns.value,
      total: total.value,
      loading: reloading.value,
      pagination: true,
      "rows-per-page": rowsPerPage.value,
      "default-sort-by": "lastSeen",
      "default-sort-direction": "desc",
      onLoad: dataTableChanged,
      onCellClicked: handleCellClick
    }, _createSlots({
      nodata: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(PersonSearchIcon, { class: "icon-2x" }),
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "no-contacts-message" }, [
            _createElementVNode("h1", null, "No contacts match the filters")
          ], -1))
        ])
      ]),
      _: 2
    }, [
      _renderList(getContactAttributesToFormat(), (attribute) => {
        return {
          name: attribute.id,
          fn: _withCtx(({ row }) => [
            (attribute.type !== 'date')
              ? (_openBlock(), _createBlock(FriendlyString, {
                  value: row[attribute.id] ? row[attribute.id].toString() : undefined,
                  key: attribute.id
                }, null, 8, ["value"]))
              : _createCommentVNode("", true),
            (attribute.type === 'date')
              ? (_openBlock(), _createBlock(FriendlyDateTime, {
                  date: row[attribute.id],
                  placeholder: !attribute.system ? 'unknown' : 'never',
                  key: attribute.id
                }, null, 8, ["date", "placeholder"]))
              : _createCommentVNode("", true)
          ])
        }
      })
    ]), 1032, ["data", "columns", "total", "loading", "rows-per-page"]),
    _createVNode(_component_router_view)
  ]))
}
}

})